import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import { buildWalletReportHtml } from './wallet-report-html';
import { md5Hex, sha256Hex, zipStore } from './wallet-export-binary.util';
import {
  A4_HEIGHT_MM,
  A4_HEIGHT_PX,
  A4_WIDTH_MM,
  A4_WIDTH_PX,
  paginateReportIntoPages,
} from './wallet-report-paginate';

@Injectable({
  providedIn: 'root',
})
export class OsintWalletExportService {
  async exportReport(options: {
    result: any;
    format: 'pdf' | 'zip';
    generatedBy?: string;
  }): Promise<void> {
    const address = String(options.result?.address || '').trim();
    if (!address) {
      throw new Error('Investigation result is required');
    }

    const html = buildWalletReportHtml(options.result, {
      generatedBy: options.generatedBy,
      logoUrl: new URL('assets/images/logo.png', document.baseURI).href,
    });
    const pdfBytes = await this.htmlToPdfBytes(html);
    const md5 = md5Hex(pdfBytes);
    const sha256 = await sha256Hex(pdfBytes);
    const baseName = `crypto-investigation-${address.slice(0, 12)}`;
    const pdfName = `${baseName}-${md5 || 'nohash'}.pdf`;

    if (options.format === 'zip') {
      const hashesTxt =
        `Ryno Crypto Investigator — Report Integrity Hashes\n` +
        `====================================================\n` +
        `File:    ${pdfName}\n` +
        `MD5:     ${md5 || 'unavailable'}\n` +
        `SHA-256: ${sha256 || 'unavailable'}\n` +
        `Generated: ${new Date().toISOString()}\n`;
      const zipBytes = zipStore([
        { name: pdfName, data: pdfBytes },
        {
          name: `${baseName}-hashes.txt`,
          data: new TextEncoder().encode(hashesTxt),
        },
      ]);
      this.downloadBytes(
        zipBytes,
        `${baseName}-${md5 || 'nohash'}.zip`,
        'application/zip',
      );
      return;
    }

    this.downloadBytes(pdfBytes, pdfName, 'application/pdf');
  }

  private async htmlToPdfBytes(html: string): Promise<Uint8Array> {
    const { host, root } = this.mountReport(html);
    try {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }
      await this.waitForImages(root);
      await this.yield();

      const pages = paginateReportIntoPages(root);
      const hasText = pages.some((page) => (page.textContent || '').trim().length > 0);
      if (!pages.length || !hasText) {
        throw new Error('Wallet PDF rendered empty');
      }

      root.style.position = 'relative';
      const canvas = await html2canvas(root, {
        scale: 1.25,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollX: 0,
        scrollY: 0,
      });
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      this.paintPageBoxes(pdf, canvas, root, pages);
      canvas.width = 0;
      canvas.height = 0;
      return new Uint8Array(pdf.output('arraybuffer'));
    } finally {
      host.remove();
    }
  }

  private mountReport(html: string): { host: HTMLElement; root: HTMLElement } {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const host = document.createElement('div');
    host.className = 'wallet-pdf-host';
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = [
      'position:fixed',
      'left:-10000px',
      'top:0',
      `width:${A4_WIDTH_PX}px`,
      'background:#ffffff',
      'opacity:1',
      'pointer-events:none',
      'z-index:-1',
    ].join(';');

    for (const styleEl of Array.from(parsed.querySelectorAll('style'))) {
      const style = document.createElement('style');
      style.textContent = styleEl.textContent || '';
      host.appendChild(style);
    }

    const root = document.createElement('div');
    root.className = 'wallet-pdf-host';
    root.style.width = `${A4_WIDTH_PX}px`;
    root.style.background = '#ffffff';
    for (const child of Array.from(parsed.body.childNodes)) {
      if (child.nodeType === 1 || child.nodeType === 3) {
        root.appendChild(document.importNode(child, true));
      }
    }
    if (!root.childElementCount) {
      throw new Error('Wallet report HTML was empty before pagination');
    }

    host.appendChild(root);
    document.body.appendChild(host);
    return { host, root };
  }

  private paintPageBoxes(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    root: HTMLElement,
    pages: HTMLElement[],
  ): void {
    const scaleY = canvas.height / Math.max(root.scrollHeight, 1);
    const sliceCanvas = document.createElement('canvas');
    const sliceCtx = sliceCanvas.getContext('2d');
    if (!sliceCtx) {
      throw new Error('Failed to paginate investigation report');
    }

    pages.forEach((page, pageIndex) => {
      const sourceY = Math.max(0, Math.round(page.offsetTop * scaleY));
      const sourceHeight = Math.min(
        canvas.height - sourceY,
        Math.round(page.offsetHeight * scaleY),
      );
      if (sourceHeight <= 0) {
        return;
      }
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = sourceHeight;
      sliceCtx.fillStyle = '#ffffff';
      sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      sliceCtx.drawImage(
        canvas,
        0,
        sourceY,
        canvas.width,
        sourceHeight,
        0,
        0,
        canvas.width,
        sourceHeight,
      );
      const jpeg = sliceCanvas.toDataURL('image/jpeg', 0.84);
      if (pageIndex > 0) {
        pdf.addPage('a4', 'portrait');
      }
      pdf.addImage(
        jpeg,
        'JPEG',
        0,
        0,
        A4_WIDTH_MM,
        A4_HEIGHT_MM,
        undefined,
        'FAST',
      );
    });

    sliceCanvas.width = 0;
    sliceCanvas.height = 0;
  }

  private waitForImages(root: HTMLElement): Promise<void> {
    const images = Array.from(root.querySelectorAll('img'));
    if (!images.length) {
      return Promise.resolve();
    }
    return Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }),
      ),
    ).then(() => undefined);
  }

  private yield(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 0));
  }

  private downloadBytes(
    bytes: Uint8Array,
    fileName: string,
    mimeType: string,
  ): void {
    const blob = new Blob([bytes as BlobPart], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }
}
